import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import pick from 'lodash/pick';
import styled, { cx, StylesProps } from '../../lib/styles';
import { connect, useSelector } from 'react-redux';
import LazyLoad from 'react-lazyload';
import { compose, onlyUpdateForKeys, withProps, withState } from 'recompose';
import memoize from '../../lib/selectorUtils';
import { actions } from './store';
import {
  articleSelector,
  adConfigSelector,
  socialMediaSelector,
  faviconSelector,
  Brand,
  AdConfig,
  SocialMedia,
  adsSelector,
  suggestedArticlesUrlSelector,
  hideCommentsSelector,
  hideStickyRecircSelector,
  hideAdsSelector,
  showLinkbyPubFeedSelector,
} from './store/selectors';
import { Article } from '../../models/article';
import Spacer from '../../components/presentational/Spacer';
import PageBody from '../../components/presentational/PageBody';
import TypeformEmbed from '../../components/presentational/TypeformEmbed';
import Metadata from './Metadata';
import { callOnMount } from '../../behaviour/patterns';

import Ad, { Ads } from '../../components/presentational/Ad';
import AdSetup from '../../components/presentational/Ad/AdSetup';
import StandardArticleLayout from './StandardArticleLayout';
import SponsoredArticleLayout from './SponsoredArticleLayout';
import ArticleHolidays from './ArticleHolidays';
import articleStyles from './Article_styles';
import { pushEvent, raiseEvent, SIGNUP_ARTICLE_COLLECTION_CHALLENGE } from '../../lib/gtm';
import GameLayout from './GameLayout';
import { getTrackingEmail } from '../../lib/accountUtils';
import { post } from '../../lib/api';
import endpoints from '../../lib/endpoints';
import { getArticleTargeting } from '../../lib/adTargeting';
import MembershipChallenge from '../../components/presentational/MembershipChallenge';
import { useLocalStorage } from '../../lib/hooks/useLocalStorage';
import dayjs from 'dayjs';
import { TrackingDataState } from '../../store/reducers/trackingData';
import theme from "../../lib/theme";

const { layoutWidths } = theme;
const HIDDEN_ADHESION = process.env.HIDDEN_ADHESION;

interface CommonArticleProps {
  brand: Brand;
}
export interface ArticleProps extends CommonArticleProps {
  article: Article;
  ads: Ads;
  socialMedia: SocialMedia;
  adConfig: AdConfig;
  faviconUrl: string;
  activeTypeform?: string;
  setActiveTypeform: (form: string) => any;
  suggestedArticlesUrl?: string;
  hideComments?: boolean;
  hideStickyRecirc?: boolean;
  hideAds?: boolean;
  showLinkbyPubFeed?: boolean;
  trackingProcessed?: boolean;
  setTrackingProcessed: (processed: boolean) => any;
}

const isAMPEnabled = ({ link, seo_canonical_url }: Article) =>
  (seo_canonical_url || link).includes('startsat60.com/');

const Article = ({
  brand,
  article,
  socialMedia,
  ads,
  adConfig,
  faviconUrl,
  activeTypeform,
  hideComments,
  hideStickyRecirc,
  hideAds,
  showLinkbyPubFeed,
  styles,
}: ArticleProps & StylesProps<typeof articleStyles>) => {
  const targeting = getArticleTargeting(article);
  const [localStorageTracking, setLocalStorageTracking] = useLocalStorage('sas', null);
  const trackingData = useSelector((state: any): TrackingDataState => (state.trackingData));
  const buyoutIsExpired = !article.advertiserBuyoutExpiration || dayjs(article.advertiserBuyoutExpiration).isBefore(dayjs(), 'day');

  useEffect(() => {
    // console.log('[Article] Tracking data:', {
    //   brand,
    //   article,
    //   socialMedia,
    //   ads,
    //   adConfig,
    // });
    if (!trackingData.isLoaded) return;
    setLocalStorageTracking({
      ...localStorageTracking,
      uuid: localStorageTracking ? localStorageTracking.uuid : Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15),
      email: trackingData?.email,
      history: [
        ...(localStorageTracking ? localStorageTracking.history.filter((h: any) => h.timestamp > dayjs().subtract(1, 'week')) : []),
        ...[{ link: article?.url, taxonomy: article.article_type, id: article.id, brand, timestamp: dayjs().valueOf() }]
      ]
    });
  }, [trackingData]);

  useEffect(() => {
    console.log('Article', { ads, buyoutIsExpired, article, advertiserBuyoutExpiration: article.advertiserBuyoutExpiration, adConfig });
  }, []);

  return (
    <PageBody>
      <Helmet titleTemplate={`%s - ${socialMedia.site}`} title={article.title}>
        {(showLinkbyPubFeed && process.env.COOKIE_NAME === 'id_token') && (<script src={`//pubfeed.linkby.com/widget.js`}></script>)}
      </Helmet>

      <AdSetup
        predefineAds={pick(
          ads,
          article.article_type === 'games' ? [
            'game_adhesion',
            // 'game_siderail1',
            // 'game_siderail2',
            // 'game_incontent1',
            // 'game_incontent2',
            // 'game_enterprise_billboard',
          ] :
            adConfig.layout === 'sponsored' ? [
              'sponsored_inarticle_native',
              'sponsored_inarticle1',
              'sponsored_inarticle2',
              'sponsored_siderail1',
              'sponsored_siderail2',
              'sponsored_adhesion',
            ] :
            [
              'standard_inarticle_video',
              'standard_inarticle_native',
              'standard_high_impact',
              'standard_billboard',
              'standard_adhesion',
              'standard_scrollad',
            ]
        )}
        pageLevelTargeting={
          [
            { key: 'page_type', value: article.article_type === 'games' ? 'game' : 'article' },
            { key: 'page_path', value: article.path || 'N/A' },
            { key: 'page_category', value: article.article_type === 'games' ? 'game' : 'article' },
            ...(targeting.slug ? [{ key: 'slug', value: targeting.slug }] : []),
            ...(targeting.page_id ? [{ key: 'page_id', value: article.id.toString() }] : []),
            ...(targeting.section ? [{ key: 'section', value: targeting.section }] : []),
            ...(targeting.tags ? [{ key: 'tags', value: targeting.tags.join(',') }] : []),
            ...(targeting.categories ? [{ key: 'categories', value: targeting.categories.join(',') }] : []),
            ...(targeting.author ? [{ key: 'author', value: targeting.author }] : []),

          ]
        }
      />

      <Metadata
        {...article}
        brand={brand}
        socialMedia={socialMedia}
        faviconUrl={faviconUrl}
        amp={isAMPEnabled(article)}
        urlBreadcrumbs={article.url_bread_crumbs}
      />

      <Spacer maxWidth={article.article_type === 'games' ? layoutWidths.wideContentPane : layoutWidths.contentPane}>
        {!hideAds && adConfig.layout === 'standard' && ads.standard_high_impact && (
          <div className={styles.hiddenAd}>
            <Ad
              ad={ads.standard_high_impact}
              gutter={false}
              inline
              predefined
              backgroundColor={'transparent'}
            />
          </div>
        )}

        <Spacer space={'m'} selfH={'xs'} align={'center'}>
          {article.article_type === 'games' ?
            (
              <GameLayout
                ads={ads}
                article={article}
                hideComments={hideComments}
                hideStickyRecirc={hideStickyRecirc}
                hideAds={hideAds}
                showLinkbyPubFeed={showLinkbyPubFeed}
              />
            ) : (
              adConfig.layout === 'sponsored' ? (
                <SponsoredArticleLayout
                  ads={ads}
                  article={article}
                  hideAds={hideAds}
                  hideComments={hideComments}
                  showLinkbyPubFeed={showLinkbyPubFeed}
                />
              ) : 
              <StandardArticleLayout
                ads={ads}
                article={article}
                hideComments={hideComments}
                hideStickyRecirc={hideStickyRecirc}
                hideAds={hideAds}
                showLinkbyPubFeed={showLinkbyPubFeed}
              />
            )
          }
        </Spacer>

        {/* {adConfig.layout === 'standard' && (
          <ContinueReading
            recirculationAd1={ads.standard_recirculation_native1}
            recirculationAd2={ads.standard_recirculation_native2}
          />
        )} */}

        {buyoutIsExpired && (
          <Spacer 
            className={'ArticleHolidays'} 
            space={'l'} 
            maxWidth={article.article_type === 'games' ? layoutWidths.wideContentPane : layoutWidths.contentPane}
          >
            <LazyLoad>
              <ArticleHolidays />
            </LazyLoad>

            {!hideAds && adConfig.layout === 'standard' && (
              <Spacer align={'center'}>
                <Ad
                  ad={article.article_type === 'games' ?
                    ads.game_enterprise_billboard :
                    ads.standard_enterprise_billboard
                  }
                  backgroundColor={'transparent'}
                  initialSize={[300, 90]}
                  lazy
                />
              </Spacer>
            )}
          </Spacer>
        )}
      </Spacer>

      {buyoutIsExpired && (
        <>
          {activeTypeform && (
            <TypeformEmbed
              popup
              autoOpen
              url={activeTypeform}
              hideHeaders
              hideFooter
            />
          )}

          {!hideAds && (
            <Spacer
              space={'m'}
              selfH={'m'}
              selfT={'m'}
              align={'center'}
              maxWidth={article.article_type === 'games' ? layoutWidths.wideContentPane : layoutWidths.contentPane}
              className={`taboola-feed`}
            >
              <div id="taboola-below-article-thumbnails"></div>
              <script
                type="text/javascript"
                dangerouslySetInnerHTML={{ __html: `
                  window._taboola = window._taboola || [];
                  _taboola.push({
                    mode: 'alternating-thumbnails-a',
                    container: 'taboola-below-article-thumbnails',
                    placement: 'Below Article Thumbnails',
                    target_type: 'mix'
                  });
                ` }}
              />
              <Ad
                className={cx(styles.recirculationAd, styles.taboolaFeedSplitAd)}
                ad={article.article_type === 'games' ?
                  ads.game_recirculation_native1 :
                  ads.standard_recirculation_native1
                }
                gutter={'s'}
                // backgroundColor="transparent"
                initialSize={['100%', 0]}
              />
              <Ad
                className={cx(styles.recirculationAd, styles.taboolaFeedSplitAd)}
                ad={article.article_type === 'games' ?
                  ads.game_recirculation_native2 :
                  ads.standard_recirculation_native2
                }
                gutter={'s'}
                // backgroundColor="transparent"
                initialSize={['100%', 0]}
              />
              <MembershipChallenge
                classes={`${styles.taboolaFeedSplitAd}`}
                onClick={() => {
                  raiseEvent(SIGNUP_ARTICLE_COLLECTION_CHALLENGE);
                }}
              />
            </Spacer>
          )}
        </>
      )}
    </PageBody>
  );
};

const articleProps = memoize(
  articleSelector,
  socialMediaSelector,
  adsSelector,
  adConfigSelector,
  faviconSelector,
  suggestedArticlesUrlSelector,
  hideCommentsSelector,
  hideStickyRecircSelector,
  hideAdsSelector,
  showLinkbyPubFeedSelector,
  (
    article,
    socialMedia,
    ads,
    adConfig,
    faviconUrl,
    suggestedArticlesUrl,
    hideComments,
    hideStickyRecirc,
    hideAds,
    showLinkbyPubFeed
  ) => ({
    article,
    socialMedia,
    ads,
    adConfig,
    faviconUrl,
    suggestedArticlesUrl,
    hideComments,
    hideStickyRecirc,
    hideAds,
    showLinkbyPubFeed,
  })
);

const dispatchProps = dispatch => ({
  loadHolidays: url => dispatch(actions.loadHolidays(url)),
  loadSuggestedArticles: url => dispatch(actions.loadSuggestedArticles(url)),
});

const withOnMount = compose(
  withState('activeTypeform', 'setActiveTypeform', undefined),
  withState('trackingProcessed', 'setTrackingProcessed', false),
  withProps(
    ({
      suggestedArticlesUrl,
      loadSuggestedArticles,
      article,
      setActiveTypeform,
      trackingProcessed,
      setTrackingProcessed,
    }) => ({
      onMount: () => {
        if (suggestedArticlesUrl) loadSuggestedArticles(suggestedArticlesUrl);
        window.addEventListener(
          'message',
          ({ data }) => {
            try {
              if (
                data.typeform &&
                data.typeform.includes('startsat60.typeform.com')
              ) {
                setActiveTypeform(data.typeform);
              }
            } catch (err) {
              // Do nothing. It was probably just a non-TypeForm message.
            }
          },
          false
        );

        const trackUser = (email) => {
          if (!trackingProcessed && email !== undefined) {
            const WAIT_BEFORE_TRACKING = 10000;
            const trackingTimer = setInterval(() => {
              const postTracking = async () => {
                post(endpoints.userTracking, {
                  email,
                  aggregator_url: article.link,
                  aggregator_text: article.category,
                })
              };
  
              //  Run the tracker once and clear the interval
              postTracking()
              .finally(() => {
                clearInterval(trackingTimer);
                setTrackingProcessed(true);
              })
            }, WAIT_BEFORE_TRACKING);
          };  
        }
        
        getTrackingEmail().then((email => trackUser(email)));

        pushEvent('travelPageView', {
          virtualPageUrl: location.href,
          pageCategory: 'Articles',
          pageSection: article.category,
          pageNumber: 1,
          pageTitle: article.slug,
        });
      },
    })
  ),
  callOnMount('onMount')
);

export default compose(
  connect(articleProps, dispatchProps),
  withOnMount,
  onlyUpdateForKeys(['activeTypeform']),
  styled(articleStyles)
)(Article);
